import ks from "./kBrowserShared";

/**
 * ⛔ DEPRECATED 2026-08-19 - DO NOT USE IN NEW CODE. THE SANCTION THIS CLASS CARRIED IS WITHDRAWN.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * THE RULING
 *
 *   "A wrong caption should create a dead app."
 *   "No softening of the rules - the global error handler is our best tool to reach perfect code."
 *
 * This class was the estate's "sanctioned degradation": a shared library could reference a phrase
 * key that a consuming product's snapshot lacked, and this call would quietly substitute a
 * fallback caption so nothing looked broken. The reasoning below (kept verbatim, because the
 * MEASUREMENTS in it are correct and still worth having) argued that was strictly better than
 * painting "undefined".
 *
 * It measured the right thing and drew the wrong conclusion. Not painting "undefined" is not the
 * goal; the goal is that the defect gets FIXED. A product whose snapshot is stale has a broken
 * build, and a fallback caption is precisely what let SPColdStorage ship
 * `aria-label="undefined"` on every modal close button for weeks with every gate green.
 * MAKING IT SURVIVABLE IS WHAT KEPT IT BROKEN.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS CLASS COULD NEVER HAVE BEEN THE ANSWER, MECHANICALLY
 *
 * `Or` receives the phrase VALUE, never the KEY. By the time it runs, `LNG.CLEAR` has already
 * been evaluated and the string "CLEAR" is gone. So this class is STRUCTURALLY INCAPABLE of
 * producing an actionable report - the best it could ever do is say "some caption somewhere did
 * not resolve", which is the mystery-blank-screen outcome, not a fix.
 *
 * `kLNGGuard` (X:\Source\Admin\ReUsableTS\kLNGGuard.ts) sits at the READ, where the key name still
 * exists, and reports it by name to `ks.KGlobalErrorHandler`. Note what that means for this class:
 * argument evaluation happens FIRST, so in `kPhrase.Or(LNG.CLEAR, "Clear")` the guard has already
 * reported `CLEAR` before `Or` is even entered. **`Or` cannot suppress anything any more.** It now
 * only decides which string is painted on an app that is already entering error mode.
 *
 * ⚠ IT IS LEFT COMPILING, NOT DELETED, ON PURPOSE. Its two call sites - `kHTMLMultiSelect.ts:805`
 * and `kFileTypePicker.Localized` - belong to files this change does not own, and deleting the
 * class would break their builds for no gain: the loudness is already delivered by the guard.
 * Remove the call sites and then this file, as a separate piece of work with those files' owner.
 * Do NOT add new call sites, and do NOT read the paragraphs below as a live sanction.
 *
 * ⚠ `Test-kLNGSnapshotCoverage.ps1` NO LONGER EXCUSES A GUARDED READ. A `kPhrase.Or`-wrapped
 * reference to a key missing from the snapshot now FAILS the build exactly like an unguarded one.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────
 * ORIGINAL RATIONALE, KEPT FOR ITS MEASUREMENTS ONLY - THE CONCLUSION IS SUPERSEDED ABOVE
 *
 * A phrase read from `LNG`, or a caller-supplied fallback when the consuming product's
 * kLNG SNAPSHOT does not carry that key.
 *
 * Shared code type-checks against the SHARED `X:\Source\Admin\ReUsableTS\kLNG.ts`, which
 * carries every key in `phrases.json`. The bundler then swaps in the PRODUCT's
 * `kLNG.snapshot.ts`, which is scoped to the keys that product's import graph REFERENCED
 * when the snapshot was last generated. So the moment a shared file references a key for
 * the FIRST time, every product whose snapshot predates that reference renders the literal
 * string "undefined" on screen — with the type check, every unit suite and the build all
 * green, because `LNG.X` is declared `string` and nothing downstream dereferences it.
 *
 * Measured 2026-08-17, moving `kHTMLMultiSelect`'s hard-coded "Clear" onto `LNG.CLEAR`, across
 * ALL 11 live `kLNG.snapshot.ts` files in the estate: FOUR carried `CLEAR` (CE Sprinkler,
 * FleetFlow, MOSE, Oigoo) and SIX did not — A3FormsBuilder, Kanban Task Manager, PDF
 * Solution2024, SPColdStorage, TemplatesManager, Test kBuild Product. Referencing it unguarded
 * would have put "undefined" on a button in SIX shipping products in order to translate it in
 * one — strictly worse than the untranslated English it replaced.
 *
 * ⚠ THE FIRST READING OF THAT NUMBER WAS "five snapshots, one carried it, three did not", and
 * it was a narrower search root reported as an estate-wide count. It understated the blast
 * radius by half. Enumerate every kLNG.snapshot.ts under X:\Source — excluding node_modules
 * and backup folders — before quoting a number, and read each file's language COUNT too: a
 * three-language snapshot (FleetFlow is sv/de/en) cannot be affected by a repair to an Arabic
 * or Norwegian value however loudly the key name matches.
 *
 * ⚠ DO NOT PUT A BACKTICKED SHELL GLOB IN THIS DOCBLOCK — IT BREAKS THE TYPE CHECK. TypeScript
 * parses JSDoc semantically, and the first draft of the paragraph above quoted a find command
 * containing a path glob in backticks; tsc resolved the directory name inside it as a type and
 * failed with TS2304 "Cannot find name". Same family as the backtick-inside-a-template-literal
 * defect this estate has paid for three times, through a different door. It fails LOUDLY, which
 * is the good case — describe commands in prose here and keep runnable ones in the skill.
 *
 * With the guard the change composes instead: a product with a stale snapshot keeps exactly
 * the caption it has today, and gains the translation the next time it regenerates. Nothing
 * has to be coordinated, and no product can regress.
 *
 * ⚠ THE `typeof` TEST IS NOT DEFENSIVE NOISE — IT IS THE WHOLE MECHANISM. `Phrase` is typed
 * `string` because `kLNG.ts` declares every key as `string`; the runtime value from a stale
 * snapshot is `undefined`. The compiler therefore cannot see this case, which is precisely
 * why it reaches the screen.
 *
 * Lifted here 2026-08-17 on the SECOND consumer (constitution rule 3). The first copy was
 * `kFileTypePicker.Localized`, written 2026-08-16 for the same trap with `LNG.OTHER` welded
 * in as the fallback; it now delegates, so the two cannot drift.
 */
export class kPhrase {
    /**
     * ⛔ DEPRECATED - see the class docblock. `Phrase` when it resolved, `Fallback` when it did not.
     *
     * This NO LONGER SUPPRESSES ANYTHING. `LNG.X` is evaluated before this call is entered, so
     * `kLNGGuard` has already reported the missing key by name and `ks.KErrorMode` is already
     * being flipped. All this now decides is which string is painted during the frame the app
     * stops in. No `ERROR-SUPPRESSED-JUSTIFIED` marker is claimed here, deliberately: that marker
     * is for a genuinely EXPECTED, DEFINED condition (a first-run file, an expected 404). A
     * missing translation is never expected - it means the pipeline did not do its job.
     */
    public static Or = (Phrase: string, Fallback: string): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (typeof Phrase !== "string") { return Fallback; }
            if (Phrase === "") { return Fallback; }
            return Phrase;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kPhrase.Or");
            return "";
        }
    };
}
